import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body) {
  if (!body?.success) {
    throw new Error(sanitizeBusinessMessage(body?.message, 'Request failed'))
  }
  return body.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type OrdersListParams = {
  page?: number
  limit?: number
}

export type OrdersListResponse = {
  items: unknown[]
  total: number
  page: number
  limit: number
}

export type StoreCustomerDto = {
  id?: number
  email?: string
  firstName?: string
  lastName?: string
}

export type StoreOrderLineProduct = {
  id: number
  name: string
  slug: string
  sku: string
  imageUrl: string | null
} | null

export type StoreOrderLineDto = {
  id: number
  productId: number
  quantity: number
  unitListPrice: string
  unitSalePrice: string
  lineTotal: string
  pricingMetadata: unknown
  product?: StoreOrderLineProduct
}

export type StoreCustomerAddressDto = {
  id: number
  label: string
  name: string
  phone: string
  street: string
  city: string
  state: string
  country: string
  zip: string
  deliveryNotes: string | null
} | null

export type StorePickupLocationDto = {
  id: number
  name: string
  addressText: string
  feeAmount: string
} | null

export type StoreOrderDetail = {
  id: number
  orderReference: string
  storeId: number
  customer?: StoreCustomerDto
  store?: { id: number; name: string; subdomain: string }
  status: string
  paymentStatus: string
  paymentMethod?: string
  currency: string
  subtotal: string
  discountTotal?: string
  discountCode?: string | null
  taxTotal: string
  taxRateAppliedPercent: string | null
  shippingTotal: string
  feeTotal: string
  total: string
  deliveryType: string
  deliveryNotes: string | null
  pickupLocation?: StorePickupLocationDto
  customerAddress: StoreCustomerAddressDto
  providerCheckoutReference: string | null
  createdAt: string
  updatedAt?: string
  lines?: StoreOrderLineDto[]
  /** Mapped for table display */
  customerName: string
  orderNumber: string
  totalAmount: string
}

function mapCustomerName(customer: unknown): string {
  if (!customer || typeof customer !== 'object') return '-'
  const c = customer as StoreCustomerDto
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim()
  return name || c.email || '-'
}

export type StoreOrderListItem = {
  id: number
  orderReference: string
  orderNumber: string | number
  customerName: string
  email?: string
  totalAmount: unknown
  subtotal: unknown
  taxTotal: unknown
  shippingTotal: unknown
  feeTotal: unknown
  status: unknown
  paymentStatus: unknown
  currency: unknown
  createdAt: string
}

function normalizeOrderListItem(raw: Record<string, unknown>): StoreOrderListItem {
  const orderReference = String(raw.orderReference ?? '')
  return {
    id: raw.id as number,
    orderReference,
    orderNumber: orderReference || (raw.id as number),
    customerName: mapCustomerName(raw.customer),
    email: (raw.customer as StoreCustomerDto | undefined)?.email,
    totalAmount: raw.total,
    subtotal: raw.subtotal,
    taxTotal: raw.taxTotal,
    shippingTotal: raw.shippingTotal,
    feeTotal: raw.feeTotal,
    status: raw.status,
    paymentStatus: raw.paymentStatus,
    currency: raw.currency,
    createdAt: String(raw.createdAt ?? ''),
  }
}

export function normalizeOrderDetail(raw: Record<string, unknown>): StoreOrderDetail {
  const orderReference = String(raw.orderReference ?? '')
  return {
    id: raw.id as number,
    orderReference,
    storeId: raw.storeId as number,
    customer: raw.customer as StoreCustomerDto | undefined,
    store: raw.store as StoreOrderDetail['store'],
    status: String(raw.status ?? ''),
    paymentStatus: String(raw.paymentStatus ?? ''),
    paymentMethod: raw.paymentMethod != null ? String(raw.paymentMethod) : undefined,
    currency: String(raw.currency ?? ''),
    subtotal: String(raw.subtotal ?? ''),
    discountTotal: raw.discountTotal != null ? String(raw.discountTotal) : '0',
    discountCode: raw.discountCode != null ? String(raw.discountCode) : null,
    taxTotal: String(raw.taxTotal ?? ''),
    taxRateAppliedPercent: (raw.taxRateAppliedPercent as string | null) ?? null,
    shippingTotal: String(raw.shippingTotal ?? ''),
    feeTotal: String(raw.feeTotal ?? ''),
    total: String(raw.total ?? ''),
    deliveryType: String(raw.deliveryType ?? ''),
    deliveryNotes: (raw.deliveryNotes as string | null) ?? null,
    pickupLocation: (raw.pickupLocation as StorePickupLocationDto) ?? null,
    customerAddress: (raw.customerAddress as StoreCustomerAddressDto) ?? null,
    providerCheckoutReference: (raw.providerCheckoutReference as string | null) ?? null,
    createdAt: raw.createdAt as string,
    updatedAt: raw.updatedAt as string | undefined,
    lines: (raw.lines as StoreOrderLineDto[]) ?? [],
    customerName: mapCustomerName(raw.customer),
    orderNumber: orderReference || String(raw.id ?? ''),
    totalAmount: String(raw.total ?? ''),
  }
}

export async function fetchOrders(params: OrdersListParams = {}): Promise<OrdersListResponse> {
  try {
    const page = params.page ?? 1
    const limit = params.limit ?? 20
    const { data } = await api.get('/storebusiness/orders', { params: { page, limit } })
    const payload = assertSuccess(data) as OrdersListResponse
    const rows = (payload.items || []) as Record<string, unknown>[]
    return {
      items: rows.map((r) => normalizeOrderListItem(r)),
      total: payload.total ?? 0,
      page: payload.page ?? page,
      limit: payload.limit ?? limit,
    }
  } catch (e) {
    throw apiError(e, 'Failed to load orders')
  }
}

export async function fetchOrderByReference(orderReference: string): Promise<StoreOrderDetail> {
  try {
    const encoded = encodeURIComponent(orderReference)
    const { data } = await api.get(`/storebusiness/orders/${encoded}`)
    const raw = assertSuccess(data) as Record<string, unknown>
    return normalizeOrderDetail(raw)
  } catch (e) {
    throw apiError(e, 'Failed to load order')
  }
}

export async function updateOrderStatus(orderReference: string, status: string) {
  try {
    const encoded = encodeURIComponent(orderReference)
    const { data } = await api.patch(`/storebusiness/orders/${encoded}/status`, { status })
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to update order status')
  }
}
